/* AE Florida Trophy Builder — Component Browse drawer (event-decoupled module).
   Self-contained, no framework. Listens for 'aetb:browse-open', opens an accessible
   dialog (focus trap, ESC + backdrop close, return focus), lazily fetches the full
   'builder-catalog.json' once (cached), renders the requested role with a debounced
   search box, role-aware filter chips, and a lazy IntersectionObserver grid.
   On pick it dispatches 'aetb:browse-select' and closes. The builder core listens.

   CONTRACT (must match ae-trophy-builder.js):
   - Open:   document.dispatchEvent(new CustomEvent('aetb:browse-open',
               {detail:{role:'columns'|'toppers'|'bases', currentHandle:'<handle>'}}))
   - Select: document.dispatchEvent(new CustomEvent('aetb:browse-select',
               {detail:{role, handle}}))
   - Catalog url resolution order (first that exists wins):
       1. window.AETB_CATALOG_URL              (string set by the core/Liquid)
       2. [data-aetb-browse-root] data-catalog-url   (asset_url emitted by Liquid)
       3. 'builder-catalog.json'               (relative default, e.g. static preview)
   The module creates [data-aetb-browse-root] itself if the core didn't render one.
*/
(function () {
  "use strict";

  var BATCH = 40;            // grid items rendered per IntersectionObserver pull
  var DEBOUNCE_MS = 140;     // search input debounce
  var EVT_OPEN = "aetb:browse-open";
  var EVT_SELECT = "aetb:browse-select";

  /* role → human copy for the drawer header + search placeholder */
  var ROLE_COPY = {
    columns: { title: "Choose a column color", placeholder: "Search columns…", noun: "columns" },
    toppers: { title: "Choose a figure", placeholder: "Search figures…", noun: "figures" },
    bases: { title: "Choose a base", placeholder: "Search bases…", noun: "bases" }
  };
  var PRIORITY_TOPPER_CATEGORIES = ["Baseball", "Basketball", "Insert Holder", "Martial Arts", "Soccer", "Tennis"];
  var BASE_FILTERS = ["White Marble", "Black Marble", "Clean White", "Clean Black", "Round"];
  var COLUMN_FILTERS = ["Round", "Rectangular"];
  var CUSTOMER_BASE_HANDLES = {
    TM3X753HCS: true,
    BK3X753HCS: true,
    BPL27W: true,
    BPL27BK: true,
    BPBR33BK: true
  };

  /* ---------- tiny helpers ---------- */
  function esc(v) {
    return String(v == null ? "" : v)
      .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;").replace(/'/g, "&#039;");
  }
  function norm(v) { return String(v == null ? "" : v).trim().toLowerCase(); }
  function debounce(fn, wait) {
    var t;
    return function () {
      var ctx = this, args = arguments;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(ctx, args); }, wait);
    };
  }
  function bySort(items) {
    return (items || []).slice().sort(function (a, b) {
      return Number(a && a.sort_order || 0) - Number(b && b.sort_order || 0);
    });
  }
  function mediumColumnLabel(label) {
    return String(label || "")
      .replace(/^\s*\d+(?:\s*\d+\/\d+)?\"?\s+/i, "")
      .replace(/\bRectanglular\b/gi, "Rectangular")
      .replace(/\s+Trophy Column\b/gi, "")
      .replace(/\s+Column\b/gi, "")
      .replace(/\s{2,}/g, " ")
      .trim();
  }
  function normalizeColumnShape(value) {
    var s = String(value || "");
    if (/rectang/i.test(s)) return "Rectangular";
    if (/\bround\b/i.test(s)) return "Round";
    if (/oval|other/i.test(s)) return "Oval/Other";
    return "";
  }
  function columnShape(item) {
    return normalizeColumnShape(item && item.shape) || normalizeColumnShape(item && item.label) || "Oval/Other";
  }
  function columnColorLabel(item) {
    var explicit = item && item.display_label;
    if (explicit) return String(explicit);
    var s = mediumColumnLabel(item && item.label);
    s = s
      .replace(/\b(Rectangular|Round|Oval)\b/gi, "")
      .replace(/\b(Buzz Saw|Twist|Sport)\b/gi, "")
      .replace(/\s{2,}/g, " ")
      .trim();
    return s || mediumColumnLabel(item && item.label) || (item && item.handle) || "";
  }
  function mediumBaseLabel(label) {
    return String(label || "")
      .replace(/\s+Countersunk\b/gi, "")
      .replace(/\s+Weighted Plastic\b/gi, "")
      .replace(/\s+(Lid|Base)\b/gi, "")
      .replace(/\s{2,}/g, " ")
      .trim();
  }
  function baseFilterLabel(item) {
    var explicit = norm(item && (item.drawer_group || item.group || item.display_label));
    if (explicit) {
      if (explicit.indexOf("white marble") >= 0) return "White Marble";
      if (explicit.indexOf("black marble") >= 0) return "Black Marble";
      if (explicit.indexOf("clean white") >= 0) return "Clean White";
      if (explicit.indexOf("clean black") >= 0) return "Clean Black";
      if (explicit.indexOf("round") >= 0) return "Round";
    }
    var text = norm([item && item.label, item && item.finish, item && item.handle].filter(Boolean).join(" "));
    if (text.indexOf("white") >= 0 && text.indexOf("marble") >= 0) return "White Marble";
    if (text.indexOf("black") >= 0 && text.indexOf("marble") >= 0) return "Black Marble";
    if (/round|rounded/.test(text)) return "Round";
    if (text.indexOf("white") >= 0) return "Clean White";
    if (text.indexOf("black") >= 0) return "Clean Black";
    return "";
  }
  function itemFilterValue(item, role) {
    if (!item) return "";
    if (role === "columns") return columnShape(item);
    if (role === "bases") return baseFilterLabel(item);
    return item.category || "";
  }
  function itemDisplayLabel(item, role) {
    if (!item) return "";
    if (role === "columns") return columnColorLabel(item);
    if (role === "bases") return item.display_label || mediumBaseLabel(item.label) || item.label;
    return item.display_label || item.label || item.handle || "";
  }
  function itemSearchText(item, role) {
    return norm([
      itemDisplayLabel(item, role),
      item && item.label,
      item && item.handle,
      item && item.category,
      item && item.finish,
      item && item.drawer_group,
      item && item.group,
      item && item.shape,
      itemFilterValue(item, role)
    ].filter(Boolean).join(" "));
  }

  /* ---------- catalog url + fetch (lazy, cached) ---------- */
  function resolveCatalogUrl(root) {
    if (window.AETB_CATALOG_URL) return String(window.AETB_CATALOG_URL);
    var attr = root && root.getAttribute("data-catalog-url");
    if (attr) return attr;
    return "builder-catalog.json";
  }

  function Browse() {
    this.root = null;           // [data-aetb-browse-root]
    this.dialog = null;
    this.backdrop = null;
    this.searchInput = null;
    this.chipsWrap = null;
    this.grid = null;
    this.sentinel = null;
    this.countEl = null;
    this.titleEl = null;

    this.catalog = null;        // cached parsed catalog
    this.catalogPromise = null; // in-flight fetch
    this.catalogUrl = null;

    this.role = null;
    this.currentHandle = null;
    this.activeCategory = null; // null = "All"
    this.query = "";
    this.filtered = [];         // current filtered+sorted item list
    this.rendered = 0;          // how many of `filtered` are in the DOM
    this.io = null;             // grid sentinel observer
    this.imgIo = null;          // (reserved) — using native loading="lazy"
    this.lastFocus = null;      // element to restore focus to on close
    this.open = false;

    this.onKeydown = this.onKeydown.bind(this);
    this.onResults = this.onResults.bind(this);
  }

  /* ---------- DOM scaffold (built once, on first open) ---------- */
  Browse.prototype.ensureRoot = function () {
    if (this.root) return;
    var root = document.querySelector("[data-aetb-browse-root]");
    if (!root) {
      root = document.createElement("div");
      root.setAttribute("data-aetb-browse-root", "");
      document.body.appendChild(root);
    }
    this.root = root;
    this.catalogUrl = resolveCatalogUrl(root);

    root.classList.add("aetb-browse");
    root.setAttribute("hidden", "");
    root.innerHTML =
      '<div class="aetb-browse-backdrop" data-browse-backdrop></div>' +
      '<div class="aetb-browse-dialog" role="dialog" aria-modal="true" ' +
        'aria-labelledby="aetb-browse-title" tabindex="-1" data-browse-dialog>' +
        '<header class="aetb-browse-head">' +
          '<h2 class="aetb-browse-title" id="aetb-browse-title" data-browse-title>Choose a component</h2>' +
          '<button type="button" class="aetb-browse-close" data-browse-close aria-label="Close">' +
            '<svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true" focusable="false">' +
            '<path d="M2 2l14 14M16 2L2 16" stroke="currentColor" stroke-width="1.6" fill="none" stroke-linecap="round"/></svg>' +
          '</button>' +
        '</header>' +
        '<div class="aetb-browse-tools">' +
          '<div class="aetb-browse-search">' +
            '<svg class="aetb-browse-sicon" width="16" height="16" viewBox="0 0 16 16" aria-hidden="true" focusable="false">' +
            '<circle cx="7" cy="7" r="5" stroke="currentColor" stroke-width="1.5" fill="none"/>' +
            '<path d="M11 11l4 4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>' +
            '<input type="search" class="aetb-browse-input" data-browse-search ' +
              'autocomplete="off" autocorrect="off" spellcheck="false" ' +
              'placeholder="Search…" aria-label="Search components">' +
          '</div>' +
          '<div class="aetb-browse-chips" data-browse-chips role="group" aria-label="Filter by category" hidden></div>' +
        '</div>' +
        '<p class="aetb-browse-count" data-browse-count aria-live="polite"></p>' +
        '<div class="aetb-browse-scroll" data-browse-scroll>' +
          '<div class="aetb-browse-grid" data-browse-grid></div>' +
          '<div class="aetb-browse-sentinel" data-browse-sentinel aria-hidden="true"></div>' +
          '<p class="aetb-browse-empty" data-browse-empty hidden>No matches. Try another search.</p>' +
        '</div>' +
      '</div>';

    this.backdrop = root.querySelector("[data-browse-backdrop]");
    this.dialog = root.querySelector("[data-browse-dialog]");
    this.titleEl = root.querySelector("[data-browse-title]");
    this.searchInput = root.querySelector("[data-browse-search]");
    this.chipsWrap = root.querySelector("[data-browse-chips]");
    this.grid = root.querySelector("[data-browse-grid]");
    this.sentinel = root.querySelector("[data-browse-sentinel]");
    this.countEl = root.querySelector("[data-browse-count]");
    this.scrollEl = root.querySelector("[data-browse-scroll]");
    this.emptyEl = root.querySelector("[data-browse-empty]");

    var self = this;

    // close affordances
    this.backdrop.addEventListener("click", function () { self.close(); });
    root.querySelector("[data-browse-close]").addEventListener("click", function () { self.close(); });

    // debounced search
    this.searchInput.addEventListener("input", debounce(function () {
      self.query = self.searchInput.value || "";
      self.applyFilter();
    }, DEBOUNCE_MS));

    // category chips (delegated)
    this.chipsWrap.addEventListener("click", function (e) {
      var chip = e.target.closest("[data-browse-cat]");
      if (!chip) return;
      var val = chip.getAttribute("data-browse-cat");
      self.activeCategory = val === "__all__" ? null : val;
      self.syncChips();
      self.applyFilter();
    });

    // grid selection (delegated)
    this.grid.addEventListener("click", function (e) {
      var card = e.target.closest("[data-browse-handle]");
      if (!card) return;
      self.select(card.getAttribute("data-browse-handle"));
    });

    // lazy-render sentinel
    if ("IntersectionObserver" in window) {
      this.io = new IntersectionObserver(function (entries) {
        for (var i = 0; i < entries.length; i++) {
          if (entries[i].isIntersecting) { self.renderMore(); }
        }
      }, { root: this.scrollEl, rootMargin: "240px 0px" });
      this.io.observe(this.sentinel);
    }
  };

  /* ---------- catalog fetch ---------- */
  Browse.prototype.loadCatalog = function () {
    if (this.catalog) return Promise.resolve(this.catalog);
    if (this.catalogPromise) return this.catalogPromise;
    var self = this;
    var url = this.catalogUrl || resolveCatalogUrl(this.root);
    this.catalogPromise = fetch(url, { credentials: "same-origin" })
      .then(function (res) {
        if (!res.ok) throw new Error("catalog HTTP " + res.status);
        return res.json();
      })
      .then(function (json) { self.catalog = json || {}; return self.catalog; })
      .catch(function (err) {
        self.catalogPromise = null; // allow a later retry
        throw err;
      });
    return this.catalogPromise;
  };

  /* role → array of items from the catalog (defensive about shape) */
  Browse.prototype.itemsForRole = function (role) {
    var cat = this.catalog || {};
    var list = cat[role];
    if (!Array.isArray(list)) list = [];
    if (role === "bases") {
      list = list.filter(function (item) {
        var handle = item && (item.handle || item.id);
        return handle && CUSTOMER_BASE_HANDLES[String(handle)];
      });
    }
    return bySort(list);
  };

  /* ---------- open / close ---------- */
  Browse.prototype.handleOpen = function (detail) {
    detail = detail || {};
    this.ensureRoot();
    this.role = detail.role || "toppers";
    this.currentHandle = detail.currentHandle != null ? String(detail.currentHandle) : null;
    this.activeCategory = null;
    this.query = "";
    this.lastFocus = document.activeElement;

    var copy = ROLE_COPY[this.role] || ROLE_COPY.toppers;
    this.titleEl.textContent = copy.title;
    this.searchInput.value = "";
    this.searchInput.setAttribute("placeholder", copy.placeholder);

    this.showShell();           // reveal the drawer immediately (perceived speed)
    this.focusInitial();
    this.setLoading(true);

    var self = this;
    this.loadCatalog().then(function () {
      if (!self.open || self.role !== (detail.role || self.role)) {
        // still consume the result for the active role
      }
      self.setLoading(false);
      self.buildChips();
      self.applyFilter();
    }).catch(function (err) {
      self.setLoading(false);
      self.showError(err);
    });
  };

  Browse.prototype.showShell = function () {
    if (this.open) return;
    this.open = true;
    this.root.removeAttribute("hidden");
    // force reflow so the CSS transition runs from the hidden state
    void this.root.offsetWidth;
    this.root.classList.add("is-open");
    document.documentElement.classList.add("aetb-browse-lock");
    document.addEventListener("keydown", this.onKeydown, true);
  };

  Browse.prototype.focusInitial = function () {
    // prefer the search box; fall back to the dialog
    try { (this.searchInput || this.dialog).focus(); } catch (e) {}
  };

  Browse.prototype.close = function () {
    if (!this.open) return;
    this.open = false;
    this.root.classList.remove("is-open");
    document.documentElement.classList.remove("aetb-browse-lock");
    document.removeEventListener("keydown", this.onKeydown, true);
    var self = this;
    var done = function () {
      if (self.open) return;            // re-opened mid-transition
      self.root.setAttribute("hidden", "");
    };
    // hide after the transition (or immediately if reduced-motion)
    var reduce = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) { done(); } else { setTimeout(done, 200); }
    var target = this.lastFocus;
    if (target && typeof target.focus === "function") {
      try { target.focus(); } catch (e) {}
    }
    this.lastFocus = null;
  };

  /* ---------- keyboard: ESC + focus trap ---------- */
  Browse.prototype.onKeydown = function (e) {
    if (!this.open) return;
    if (e.key === "Escape" || e.keyCode === 27) {
      e.preventDefault();
      this.close();
      return;
    }
    if (e.key === "Tab" || e.keyCode === 9) {
      var f = this.focusable();
      if (!f.length) { e.preventDefault(); try { this.dialog.focus(); } catch (x) {} return; }
      var first = f[0], last = f[f.length - 1];
      var active = document.activeElement;
      if (e.shiftKey) {
        if (active === first || active === this.dialog || !this.dialog.contains(active)) {
          e.preventDefault(); last.focus();
        }
      } else {
        if (active === last) { e.preventDefault(); first.focus(); }
      }
    }
  };
  Browse.prototype.focusable = function () {
    var sel = 'a[href],button:not([disabled]),input:not([disabled]),' +
      'select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';
    return [].slice.call(this.dialog.querySelectorAll(sel)).filter(function (el) {
      return el.offsetParent !== null || el === document.activeElement;
    });
  };

  /* ---------- role filter chips ---------- */
  Browse.prototype.buildChips = function () {
    var items = this.itemsForRole(this.role);
    var cats = [];
    var seen = {};
    for (var i = 0; i < items.length; i++) {
      var c = itemFilterValue(items[i], this.role);
      if (c == null || c === "") continue;
      var key = String(c);
      if (!seen[key]) { seen[key] = true; cats.push(key); }
    }
    if (this.role === "columns") {
      cats = COLUMN_FILTERS.slice();
    } else if (this.role === "bases") {
      cats = BASE_FILTERS.filter(function (cat) { return seen[cat]; });
    } else {
      cats.sort(function (a, b) { return a.localeCompare(b); });
    }
    this.categories = cats;
    if (!cats.length) {
      this.chipsWrap.setAttribute("hidden", "");
      this.chipsWrap.innerHTML = "";
      return;
    }
    var html = '<button type="button" class="aetb-browse-chip is-on" ' +
      'data-browse-cat="__all__" aria-pressed="true">All</button>';
    if (this.role === "toppers") {
      var priority = PRIORITY_TOPPER_CATEGORIES.filter(function (cat) { return seen[cat]; });
      var prioritySeen = {};
      priority.forEach(function (cat) { prioritySeen[cat] = true; });
      var remaining = cats.filter(function (cat) { return !prioritySeen[cat]; });
      for (var p = 0; p < priority.length; p++) {
        html += '<button type="button" class="aetb-browse-chip" data-browse-cat="' +
          esc(priority[p]) + '" aria-pressed="false">' + esc(priority[p]) + "</button>";
      }
      if (remaining.length) {
        html += '<details class="aetb-browse-morecats"><summary>More categories</summary><div class="aetb-browse-morecat-grid">';
        for (var r = 0; r < remaining.length; r++) {
          html += '<button type="button" class="aetb-browse-chip" data-browse-cat="' +
            esc(remaining[r]) + '" aria-pressed="false">' + esc(remaining[r]) + "</button>";
        }
        html += "</div></details>";
      }
    } else {
      for (var j = 0; j < cats.length; j++) {
        html += '<button type="button" class="aetb-browse-chip" data-browse-cat="' +
          esc(cats[j]) + '" aria-pressed="false">' + esc(cats[j]) + "</button>";
      }
    }
    this.chipsWrap.innerHTML = html;
    this.chipsWrap.removeAttribute("hidden");
  };
  Browse.prototype.syncChips = function () {
    var active = this.activeCategory;
    [].forEach.call(this.chipsWrap.querySelectorAll("[data-browse-cat]"), function (chip) {
      var val = chip.getAttribute("data-browse-cat");
      var on = (val === "__all__" && active == null) || val === active;
      chip.classList.toggle("is-on", on);
      chip.setAttribute("aria-pressed", on ? "true" : "false");
    });
  };

  /* ---------- filter + lazy render ---------- */
  Browse.prototype.applyFilter = function () {
    var items = this.itemsForRole(this.role);
    var q = norm(this.query);
    var cat = this.activeCategory;
    var role = this.role;
    this.filtered = items.filter(function (it) {
      if (!it) return false;
      if (cat != null && String(itemFilterValue(it, role)) !== cat) return false;
      if (q && itemSearchText(it, role).indexOf(q) === -1) return false;
      return true;
    });
    this.rendered = 0;
    this.grid.innerHTML = "";
    this.grid.classList.toggle("aetb-browse-grid--column-lanes", this.isColumnLaneMode());
    this.updateCount();
    this.emptyEl.hidden = this.filtered.length !== 0;
    this.scrollEl.scrollTop = 0;
    if (this.isColumnLaneMode()) {
      this.sentinel.hidden = true;
      this.renderColumnLanes();
      return;
    }
    this.sentinel.hidden = false;
    this.renderMore();
    // if the first batch didn't fill the viewport, the sentinel won't fire — top it up
    this.fillViewport();
  };

  Browse.prototype.isColumnLaneMode = function () {
    return this.role === "columns" && this.activeCategory == null;
  };

  Browse.prototype.renderColumnLanes = function () {
    var self = this;
    var lanes = COLUMN_FILTERS.slice();
    var grouped = {};
    lanes.forEach(function (shape) { grouped[shape] = []; });
    this.filtered.forEach(function (item) {
      var shape = columnShape(item);
      grouped[grouped[shape] ? shape : "Rectangular"].push(item);
    });

    var frag = document.createDocumentFragment();
    lanes.forEach(function (shape) {
      var lane = document.createElement("div");
      lane.className = "aetb-browse-lane";
      lane.setAttribute("role", "group");
      lane.setAttribute("aria-label", shape + " columns");

      var title = document.createElement("h3");
      title.className = "aetb-browse-lane-title";
      title.textContent = shape;
      lane.appendChild(title);

      var items = grouped[shape] || [];
      if (items.length) {
        items.forEach(function (item) { lane.appendChild(self.card(item)); });
      } else {
        var empty = document.createElement("p");
        empty.className = "aetb-browse-lane-empty";
        empty.textContent = "No matches";
        lane.appendChild(empty);
      }
      frag.appendChild(lane);
    });

    this.grid.appendChild(frag);
    this.rendered = this.filtered.length;
  };

  Browse.prototype.renderMore = function () {
    if (this.rendered >= this.filtered.length) return;
    var end = Math.min(this.rendered + BATCH, this.filtered.length);
    var frag = document.createDocumentFragment();
    for (var i = this.rendered; i < end; i++) {
      frag.appendChild(this.card(this.filtered[i]));
    }
    this.grid.appendChild(frag);
    this.rendered = end;
    // keep the sentinel after the grid so the observer can re-fire
    if (this.sentinel.previousSibling !== this.grid) {
      this.scrollEl.appendChild(this.sentinel);
    }
  };

  // when results are short, the IO sentinel may already be on-screen and not re-fire.
  Browse.prototype.fillViewport = function () {
    var guard = 0;
    while (this.rendered < this.filtered.length && guard < 50) {
      var sRect = this.sentinel.getBoundingClientRect();
      var cRect = this.scrollEl.getBoundingClientRect();
      if (sRect.top > cRect.bottom + 240) break;
      this.renderMore();
      guard++;
    }
  };

  Browse.prototype.card = function (item) {
    var handle = item.handle || item.id || "";
    var btn = document.createElement("button");
    btn.type = "button";
    btn.className = "aetb-browse-card";
    btn.setAttribute("data-browse-handle", String(handle));
    if (this.currentHandle != null && String(handle) === this.currentHandle) {
      btn.classList.add("is-current");
      btn.setAttribute("aria-current", "true");
    }
    var label = itemDisplayLabel(item, this.role);
    var meta = "";
    if (this.role === "columns") {
      meta = columnShape(item) + " column";
    } else if (this.role === "bases") {
      meta = "";
    } else {
      meta = item.category || "";
    }
    var swatch = (this.role === "columns" && item.swatch)
      ? '<span class="aetb-browse-swatch" style="background:' + esc(item.swatch) + '"></span>'
      : "";
    var imgHtml = item.image
      ? '<img src="' + esc(item.image) + '" alt="" loading="lazy" decoding="async">'
      : '<span class="aetb-browse-noimg" aria-hidden="true"></span>';
    btn.innerHTML =
      '<span class="aetb-browse-thumb aetb-browse-thumb--' + esc(this.role) + '">' + imgHtml + "</span>" +
      '<span class="aetb-browse-meta">' + swatch +
        '<b class="aetb-browse-label">' + esc(label) + "</b>" +
        (meta ? '<span class="aetb-browse-sub">' + esc(meta) + "</span>" : "") +
      "</span>";
    return btn;
  };

  Browse.prototype.updateCount = function () {
    var copy = ROLE_COPY[this.role] || ROLE_COPY.toppers;
    var n = this.filtered.length;
    this.countEl.textContent = n
      ? n + " " + copy.noun + (this.activeCategory ? " in " + this.activeCategory : "")
      : "";
  };

  /* ---------- selection ---------- */
  Browse.prototype.select = function (handle) {
    if (!handle) return;
    document.dispatchEvent(new CustomEvent(EVT_SELECT, {
      detail: { role: this.role, handle: String(handle) }
    }));
    this.close();
  };

  /* ---------- loading / error states ---------- */
  Browse.prototype.setLoading = function (on) {
    if (on) {
      this.emptyEl.hidden = true;
      this.grid.innerHTML = "";
      this.countEl.textContent = "Loading…";
      this.root.classList.add("is-loading");
    } else {
      this.root.classList.remove("is-loading");
    }
  };
  Browse.prototype.showError = function (err) {
    this.grid.innerHTML = "";
    this.countEl.textContent = "";
    this.emptyEl.hidden = false;
    this.emptyEl.textContent = "Could not load the full catalog. Please try again.";
    if (window.console && console.warn) console.warn("[aetb-browse] catalog load failed:", err);
  };

  /* CustomEvent shim for very old browsers (defensive; modern is fine) */
  if (typeof window.CustomEvent !== "function") {
    var CE = function (event, params) {
      params = params || { bubbles: false, cancelable: false, detail: null };
      var evt = document.createEvent("CustomEvent");
      evt.initCustomEvent(event, params.bubbles, params.cancelable, params.detail);
      return evt;
    };
    CE.prototype = window.Event.prototype;
    window.CustomEvent = CE;
  }

  Browse.prototype.onResults = function () {}; // reserved hook

  /* ---------- wire the singleton to the open event ---------- */
  var instance = new Browse();
  document.addEventListener(EVT_OPEN, function (e) {
    instance.handleOpen(e && e.detail);
  });

  // expose for debugging / the core to introspect if needed
  window.AETBBrowse = instance;
})();
